/**
 * Portal detection — determines which portal the user is on based on URL.
 *
 * Production/QA/Dev:
 *   Clinician: dev.homleyehr.com, qa.homleyehr.com, app.homleyehr.com
 *   Patient:   dev-patient.homleyehr.com, qa-patient.homleyehr.com, patient.homleyehr.com
 *
 * Local development:
 *   Default is clinician portal.
 *   To test patient portal locally, set VITE_PORTAL=patient in .env.development
 *   or visit localhost:3000/patient/login
 */

export type PortalType = 'clinician' | 'patient'

export const detectPortal = (): PortalType => {
  // Check env var override first (for local dev)
  const envPortal = import.meta.env.VITE_PORTAL as string | undefined
  if (envPortal === 'patient') return 'patient'

  // Check hostname for "patient" keyword
  const hostname = window.location.hostname
  if (hostname.includes('patient')) return 'patient'

  // Check URL path for /patient/
  const pathname = window.location.pathname
  if (pathname.startsWith('/patient')) return 'patient'

  return 'clinician'
}

export const isPatientPortal = (): boolean => detectPortal() === 'patient'
export const isClinicianPortal = (): boolean => detectPortal() === 'clinician'

/**
 * Maps the current (clinician) host to its patient-portal origin so links
 * shared with clients land on the patient app for the same environment:
 *
 *   dev.homleyehr.com   → dev-patient.homleyehr.com
 *   qa.homleyehr.com    → qa-patient.homleyehr.com
 *   app.homleyehr.com   → patient.homleyehr.com
 *   localhost           → localhost (patient portal is path-based locally)
 *
 * Returns the full origin (protocol + host + port), e.g.
 * "https://dev-patient.homleyehr.com". Already-patient hosts are returned
 * unchanged so this is safe to call from either portal.
 */
export const patientPortalOrigin = (): string => {
  const { protocol, hostname, port, origin } = window.location

  // Local dev (localhost / 127.0.0.1 / *.local) keeps the same origin — the
  // patient portal is reached via the /patient path, not a separate host.
  const isLocal =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.local')
  if (isLocal) return origin

  // Already on a patient host — nothing to remap.
  if (hostname.includes('patient')) return origin

  // app.homleyehr.com (production) → patient.homleyehr.com
  // dev./qa. (and any other env prefix) → <prefix>-patient.
  const patientHostname = hostname.startsWith('app.')
    ? hostname.replace(/^app\./, 'patient.')
    : hostname.replace(/^([^.]+)\./, '$1-patient.')

  return `${protocol}//${patientHostname}${port ? `:${port}` : ''}`
}
